const REDIS_PRESENCE_URL =
  'https://www.planetspark.in/api/v1s/classroom/prebuilt/room_presence_redis';

export interface RoomPresenceResult {
  hasNonOwners: boolean;
  canJoin?: boolean;
  isRoomActive?: boolean;
  totalCount: number;
  error?: string;
}

type PresenceAction = 'join' | 'leave' | 'check';

export const REDIS_PRESENCE_POLL_MIN_MS = 30_000;
export const REDIS_PRESENCE_POLL_MAX_MS = 45_000;

export const getRandomPollInterval = (minMs: number, maxMs: number) =>
  Math.floor(minMs + Math.random() * (maxMs - minMs));

export const canJoinFromPresence = (result: RoomPresenceResult) =>
  result.canJoin ?? result.hasNonOwners;

const buildPresenceFormData = (
  roomId?: string,
  roomName?: string,
  action: PresenceAction = 'check',
  userId?: string,
  isOwner?: boolean
) => {
  const formData = new FormData();
  formData.append('room_id', roomId ?? '');
  formData.append('room_name', roomName ?? '');
  formData.append('meeting_platform', 'dailyco');
  formData.append('api_token', 'Dy2MwYGdmDxxB9PZQE7yyfVBpWix7zWMrpgX');
  formData.append('app_key', 'DailyCoPrebuilt');
  formData.append('app_version', '1.0');
  formData.append('action_type', action);
  formData.append('is_owner', isOwner ? 'true' : 'false');
  if (userId) {
    formData.append('user_id', userId);
  }
  return formData;
};

const postRedisPresence = async (
  roomId: string | undefined,
  roomName: string | undefined,
  action: PresenceAction,
  userId?: string,
  isOwner?: boolean
): Promise<RoomPresenceResult> => {
  const response = await fetch(REDIS_PRESENCE_URL, {
    method: 'POST',
    body: buildPresenceFormData(roomId, roomName, action, userId, isOwner),
  });

  if (!response.ok) {
    return {
      hasNonOwners: false,
      canJoin: false,
      isRoomActive: false,
      totalCount: 0,
      error: `API error: ${response.status}`,
    };
  }

  return response.json();
};

/**
 * Registers the current user in the Redis-backed room presence cache.
 */
export async function registerRoomPresenceRedis(
  roomId?: string,
  roomName?: string,
  userId?: string,
  isOwner?: boolean
): Promise<RoomPresenceResult> {
  return postRedisPresence(roomId, roomName, 'join', userId, isOwner);
}

/**
 * Removes one matching entry for the current user from Redis room presence.
 */
export async function leaveRoomPresenceRedis(
  roomId?: string,
  roomName?: string,
  userId?: string,
  options?: { keepalive?: boolean; isOwner?: boolean }
): Promise<RoomPresenceResult> {
  const response = await fetch(REDIS_PRESENCE_URL, {
    method: 'POST',
    body: buildPresenceFormData(
      roomId,
      roomName,
      'leave',
      userId,
      options?.isOwner
    ),
    keepalive: options?.keepalive,
  });

  if (!response.ok) {
    return {
      hasNonOwners: false,
      canJoin: false,
      isRoomActive: false,
      totalCount: 0,
      error: `API error: ${response.status}`,
    };
  }

  return response.json();
}

/**
 * Fetches room presence from the Redis-backed cache endpoint.
 */
export async function fetchRoomPresenceRedis(
  roomId?: string,
  roomName?: string,
  userId?: string,
  isOwner?: boolean
): Promise<RoomPresenceResult> {
  return postRedisPresence(roomId, roomName, 'check', userId, isOwner);
}
